import {
    type ChatInvestmentData,
    type ExistingAccountEntry,
    type IntakeInvestment,
    type InvestmentWaterfallAccountType,
    type InvestmentWaterfallOwnership,
    type PersistedInvestmentWaterfall,
} from '@fruitful/types';

import { INVESTMENT_RETURN_PERCENT, IRA_ANNUAL_LIMIT_CENTS } from './constants';

/** The tax year these IRA limits and phase-out thresholds are based on. */
export const TAX_YEAR = 2026;

const IRA_CATCHUP_AGE = 50;
/** IRA catch-up contribution for age 50+ — $1,100 (tax year 2026). */
const IRA_CATCHUP_CONTRIBUTION_CENTS = 110_000;

function iraLimitForAge(age: number): number {
    return age >= IRA_CATCHUP_AGE
        ? IRA_ANNUAL_LIMIT_CENTS + IRA_CATCHUP_CONTRIBUTION_CENTS
        : IRA_ANNUAL_LIMIT_CENTS;
}

export type RothEligibility = 'direct' | 'partial' | 'backdoor';

export interface InvestmentWaterfallAccount {
    id: string;
    memberLabel: string;
    accountType: InvestmentWaterfallAccountType;
    ownership: InvestmentWaterfallOwnership;
    rothEligibility: RothEligibility;
    annualLimitCents: number | null;
    /** Recurring annual contribution allocated to this account. */
    annualAllocationCents: number;
    /**
     * One-time planned starting cash allocated to this account (Roth-first
     * up to the annual limit, remainder to brokerage).
     */
    startingCashCents: number;
    /**
     * Existing Roth IRA balance seeded onto Roth / Backdoor Roth rows.
     * Assumes no in-year contributions have been made yet (full annual
     * limit remains available for new money).
     */
    existingBalanceCents: number;
    projectedBalanceCents: number;
    label: string;
    notes: string[];
}

export interface FeeComparisonRow {
    provider: string;
    feePercent: number;
    totalFeesCents: number;
    endBalanceCents: number;
}

export interface FeeComparison {
    rows: FeeComparisonRow[];
    years: number;
    startingBalanceCents: number;
    annualContributionCents: number;
    assumedReturnPercent: number;
}

export interface RothEligibilityDetails {
    filingStatus: string;
    incomeCents: number;
    bufferedIncomeCents: number;
    phaseOutStartCents: number;
    useBackdoor: boolean;
}

/**
 * An existing Traditional IRA that will move to Fruitful.
 * Projected balance compounds at the assumed return with no new contributions.
 */
export interface ExistingIraAccount {
    id: string;
    /** e.g. "Your existing Traditional IRA" / "Partner's existing Traditional IRA" */
    label: string;
    /** e.g. "Traditional IRA" */
    accountTypeLabel: string;
    startingBalanceCents: number;
    projectedBalanceCents: number;
}

export interface InvestmentWaterfallResult {
    accounts: InvestmentWaterfallAccount[];
    /**
     * Existing Traditional IRAs with a non-zero balance (shown as
     * "Accounts moving to Fruitful"). Empty when none.
     */
    existingIraAccounts: ExistingIraAccount[];
    totalAnnualContributionCents: number;
    /** Existing IRA balances (Traditional + Roth). */
    startingBalanceCents: number;
    /**
     * Cash the member plans to invest now — allocated Roth-first then
     * brokerage. Included in the "Starting balance" UI total with
     * startingBalanceCents.
     */
    plannedStartingCashContributionCents: number;
    projectedTotalCents: number;
    projectedDate: string;
    projectedYears: number;
    annualReturnPercent: number;
    feeComparison: FeeComparison;
    rothDetails: RothEligibilityDetails;
}

/**
 * Local dollar formatter — @fruitful/fruitils can't be a dependency of
 * this package because it's TS-source-only and breaks the Node build.
 */
export function formatDollarsLocal(amount: number, showCents = true): string {
    const abs = Math.abs(amount);
    const formatted = showCents
        ? abs.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
          })
        : Math.round(abs).toLocaleString('en-US');
    return `${amount < 0 ? '-' : ''}$${formatted}`;
}

/**
 * Roth IRA MAGI phase-out start thresholds in cents (tax year 2026).
 * Source: IRS Notice 2025-75.
 */
const ROTH_PHASE_OUT_START = {
    single: 15_300_000,
    married_filing_jointly: 24_200_000,
    married_filing_separately: 0,
    head_of_household: 15_300_000,
} as const;

/** Buffer multiplier — if income * multiplier hits the phase-out, recommend backdoor. */
const INCOME_BUFFER_MULTIPLIER = 1;

type FilingKey = keyof typeof ROTH_PHASE_OUT_START;

function resolveFilingKey(investment: IntakeInvestment): FilingKey {
    if (!investment.isJoint) return 'single';
    const status = investment.filingStatus;
    if (
        status === 'married_filing_jointly' ||
        status === 'married_filing_separately' ||
        status === 'single' ||
        status === 'head_of_household'
    ) {
        return status;
    }
    // not_sure / missing → assume MFJ (most common for joint households)
    return 'married_filing_jointly';
}

/** Default date-slider horizon (years from today). */
export const DEFAULT_PROJECTION_YEARS = 10;
export const DEFAULT_MEMBER_AGE = 30;
/** Slider max is years until this age (rounded up to a multiple of 5). */
const TARGET_AGE = 85;

export function computeDefaultProjectedYears(investment: {
    members: Array<{ age: number }>;
}): number {
    const primaryAge = investment.members[0]?.age ?? DEFAULT_MEMBER_AGE;
    const yearsToTarget = TARGET_AGE - primaryAge;
    const maxYears = Math.max(0, Math.ceil(yearsToTarget / 5) * 5);
    return Math.min(DEFAULT_PROJECTION_YEARS, maxYears);
}

/** Default annual contribution as a fraction of household pre-tax income. */
export const DEFAULT_CONTRIBUTION_RATE = 0.15;

/** Monthly contribution slider step ($50). Used when initializing the default. */
export const MONTHLY_CONTRIBUTION_STEP_CENTS = 5_000;

/**
 * Snap an annual contribution so the implied monthly amount lands on the
 * nearest {@link MONTHLY_CONTRIBUTION_STEP_CENTS} ($50) step.
 */
export function roundAnnualContributionToNearestMonthlyStep(
    annualCents: number,
): number {
    if (annualCents <= 0) return 0;
    const monthlyCents = Math.round(annualCents / 12);
    const roundedMonthly =
        Math.round(monthlyCents / MONTHLY_CONTRIBUTION_STEP_CENTS) *
        MONTHLY_CONTRIBUTION_STEP_CENTS;
    return roundedMonthly * 12;
}

/**
 * Default recurring annual contribution: 15% of pre-tax household income,
 * rounded so monthly contribution is on a $50 step. Falls back to an
 * existing positive `annualContributionCents` when income is missing or
 * non-positive.
 */
export function resolveDefaultAnnualContributionCents(investment: {
    annualHouseholdPreTaxIncomeCents?: number | null;
    annualContributionCents?: number | null;
}): number {
    const incomeCents = investment.annualHouseholdPreTaxIncomeCents;
    if (incomeCents != null && incomeCents > 0) {
        return roundAnnualContributionToNearestMonthlyStep(
            Math.round(incomeCents * DEFAULT_CONTRIBUTION_RATE),
        );
    }
    const existing = investment.annualContributionCents;
    if (existing != null && existing > 0) {
        return existing;
    }
    return 0;
}

/** Populate slider defaults from chat data. */
export function initIntakeInvestment(
    chat: ChatInvestmentData,
): IntakeInvestment {
    return {
        ...chat,
        annualContributionCents: resolveDefaultAnnualContributionCents(chat),
        projectedYears: computeDefaultProjectedYears(chat),
    };
}

/**
 * Future value of a starting lump sum plus an ordinary annuity of monthly
 * contributions, both compounding at the same rate.
 *
 * FV = PV * (1 + r)^n  +  PMT * ((1 + r)^n - 1) / r
 */
export function computeProjectedTotal(
    monthlyContributionCents: number,
    annualReturnPercent: number,
    years: number,
    startingBalanceCents = 0,
): number {
    if (years <= 0) return startingBalanceCents;
    const monthlyRate = annualReturnPercent / 100 / 12;
    const months = years * 12;

    const compoundedStart =
        monthlyRate === 0
            ? startingBalanceCents
            : startingBalanceCents * (1 + monthlyRate) ** months;

    const annuity =
        monthlyContributionCents <= 0
            ? 0
            : monthlyRate === 0
              ? monthlyContributionCents * months
              : monthlyContributionCents *
                (((1 + monthlyRate) ** months - 1) / monthlyRate);

    return Math.round(compoundedStart + annuity);
}

const FEE_COMPARISON_YEARS = 10;
const FEE_TIERS: Array<{ provider: string; feePercent: number }> = [
    { provider: 'Fruitful', feePercent: 0 },
    { provider: 'Robo-Advisors', feePercent: 0.25 },
    { provider: 'Traditional advisors', feePercent: 1.0 },
];

/**
 * Compute cumulative management fees over a fixed period for each provider
 * tier. Each month the portfolio grows at the gross return rate, then the
 * AUM-based fee is deducted from the balance — matching how fees are
 * actually assessed in practice.
 */
function computeFeeComparison(
    startingBalanceCents: number,
    annualContributionCents: number,
): FeeComparison {
    const monthlyContribution = Math.round(annualContributionCents / 12);
    const months = FEE_COMPARISON_YEARS * 12;
    const monthlyGrossRate = INVESTMENT_RETURN_PERCENT / 100 / 12;

    const rows: FeeComparisonRow[] = FEE_TIERS.map(
        ({ provider, feePercent }) => {
            const monthlyFeeRate = feePercent / 100 / 12;
            let balance = startingBalanceCents;
            let totalFees = 0;

            for (let m = 0; m < months; m++) {
                balance =
                    balance * (1 + monthlyGrossRate) + monthlyContribution;
                const fee = balance * monthlyFeeRate;
                totalFees += fee;
                balance -= fee;
            }

            return {
                provider,
                feePercent,
                totalFeesCents: Math.round(totalFees),
                endBalanceCents: Math.round(balance),
            };
        },
    );

    return {
        rows,
        years: FEE_COMPARISON_YEARS,
        startingBalanceCents,
        annualContributionCents,
        assumedReturnPercent: INVESTMENT_RETURN_PERCENT,
    };
}

function formatProjectedDate(years: number): string {
    const target = new Date();
    target.setFullYear(target.getFullYear() + years);
    return target.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });
}

/**
 * Split `totalCents` evenly across buckets, each capped at `limitsCents[i]`.
 * When one bucket hits its limit, remaining dollars keep splitting across
 * buckets that still have room (so primary + partner IRAs fill together).
 * Returns per-bucket allocations; any leftover above all limits is not
 * included (caller sends that to brokerage).
 */
export function allocateEquallyUpToLimits(
    totalCents: number,
    limitsCents: readonly number[],
): number[] {
    const allocations = limitsCents.map(() => 0);
    if (totalCents <= 0 || limitsCents.length === 0) {
        return allocations;
    }

    let remaining = totalCents;
    const open = new Set(limitsCents.map((_, index) => index));

    while (remaining > 0 && open.size > 0) {
        const share = Math.floor(remaining / open.size);

        if (share === 0) {
            for (const index of open) {
                if (remaining <= 0) break;
                const limit = limitsCents[index];
                const current = allocations[index];
                if (limit == null || current == null) {
                    open.delete(index);
                    continue;
                }
                const room = limit - current;
                if (room <= 0) {
                    open.delete(index);
                    continue;
                }
                allocations[index] = current + 1;
                remaining -= 1;
                if (current + 1 >= limit) {
                    open.delete(index);
                }
            }
            continue;
        }

        let distributed = 0;
        for (const index of [...open]) {
            const limit = limitsCents[index];
            const current = allocations[index];
            if (limit == null || current == null) {
                open.delete(index);
                continue;
            }
            const room = limit - current;
            const add = Math.min(share, room);
            allocations[index] = current + add;
            distributed += add;
            if (current + add >= limit) {
                open.delete(index);
            }
        }

        remaining -= distributed;
        if (distributed === 0) break;
    }

    return allocations;
}

/**
 * Build a prioritized investment waterfall from intake data.
 *
 * Accounts are ordered:
 *   1. Your retirement — Roth IRA or Backdoor Roth IRA (omitted when
 *      backdoor-eligible with a funded traditional IRA — pro-rata rule)
 *   2. Partner's retirement — Roth IRA (joint only, same exclusion rule)
 *   3. Joint/Your brokerage — taxable brokerage (overflow + excluded IRA $)
 *
 * Recurring annual contributions and planned starting cash are allocated
 * independently. For joint memberships, IRA dollars are split evenly across
 * members (filling together up to each annual limit), then overflow goes to
 * brokerage. Cash does not reduce the recurring /yr allocation shown on each
 * account. Existing Roth balances seed the Roth/Backdoor rows (assume no YTD
 * contributions against the annual limit).
 *
 * Uses `investment.projectedYears` when set (written by the slider),
 * otherwise falls back to `computeDefaultProjectedYears`.
 */
/** Persistable subset of {@link InvestmentWaterfallResult} for Mastra thread results. */
export function toPersistedInvestmentWaterfall(
    waterfall: InvestmentWaterfallResult,
): PersistedInvestmentWaterfall {
    return {
        useBackdoor: waterfall.rothDetails.useBackdoor,
        accounts: waterfall.accounts.map((account) => ({
            accountType: account.accountType,
            label: account.label,
            ownership: account.ownership,
        })),
    };
}

export function buildInvestmentWaterfall(
    investment: IntakeInvestment,
): InvestmentWaterfallResult {
    const years = investment.projectedYears;
    const annualReturnPercent = INVESTMENT_RETURN_PERCENT;

    const totalCapacityCents = investment.annualContributionCents;
    const plannedCashCents = Math.max(
        0,
        investment.plannedStartingCashContributionCents ?? 0,
    );
    const incomeCents = investment.annualHouseholdPreTaxIncomeCents ?? 0;
    const filingKey = resolveFilingKey(investment);
    const phaseOutStart = ROTH_PHASE_OUT_START[filingKey];
    const useBackdoor = incomeCents * INCOME_BUFFER_MULTIPLIER >= phaseOutStart;
    const rothEligibility: RothEligibility = useBackdoor
        ? 'backdoor'
        : 'direct';

    const existingAccountsList: ExistingAccountEntry[] =
        investment.existingAccounts ?? [];

    function sumAccountBalance(type: string, ownership: string): number {
        return existingAccountsList
            .filter((a) => a.accountType === type && a.ownership === ownership)
            .reduce((sum, a) => sum + Math.max(0, a.balanceCents ?? 0), 0);
    }

    const ownershipKeys = investment.isJoint
        ? (['primary', 'partner'] as const)
        : (['primary'] as const);

    // Per-member: when backdoor-eligible, skip the IRA for any member whose
    // funded traditional IRA makes the backdoor strategy impractical (pro-rata
    // rule). Their allocation flows entirely to the taxable brokerage.
    const memberExcludedFromIra = ownershipKeys.map((ownerKey: string) => {
        if (!useBackdoor) return false;
        return sumAccountBalance('ira', ownerKey) > 0;
    });

    const memberIraLimits = investment.members.map((member, index) => {
        if (memberExcludedFromIra[index]) return 0;
        return iraLimitForAge(member.age);
    });

    // Allocate planned starting cash evenly across member IRAs (up to each
    // annual limit), then brokerage. Independent of the recurring waterfall.
    const iraCashCents = allocateEquallyUpToLimits(
        plannedCashCents,
        memberIraLimits,
    );
    const brokerageCashCents = Math.max(
        0,
        plannedCashCents - iraCashCents.reduce((sum, cents) => sum + cents, 0),
    );

    // Allocate recurring annual contributions evenly across member IRAs,
    // then brokerage.
    const iraAnnualCents = allocateEquallyUpToLimits(
        totalCapacityCents,
        memberIraLimits,
    );
    const brokerageAnnualCents = Math.max(
        0,
        totalCapacityCents -
            iraAnnualCents.reduce((sum, cents) => sum + cents, 0),
    );

    const accounts: InvestmentWaterfallAccount[] = [];

    ownershipKeys.forEach((ownerKey, index) => {
        if (memberExcludedFromIra[index]) return;

        const memberIraLimit = memberIraLimits[index] ?? 0;
        const annualAllocation = iraAnnualCents[index] ?? 0;

        const accountTypeLabel = useBackdoor ? 'Backdoor Roth IRA' : 'Roth IRA';
        const existingRothCents = sumAccountBalance('roth-ira', ownerKey);

        accounts.push({
            id: `ira-${accounts.length}`,
            // Subtitle: account type (e.g. "Backdoor Roth IRA")
            memberLabel: accountTypeLabel,
            accountType: useBackdoor ? 'backdoor_roth_ira' : 'roth_ira',
            ownership: ownerKey,
            rothEligibility,
            annualLimitCents: memberIraLimit,
            annualAllocationCents: annualAllocation,
            startingCashCents: iraCashCents[index] ?? 0,
            existingBalanceCents: existingRothCents,
            projectedBalanceCents: 0,
            // Title: ownership (e.g. "Your retirement")
            label: index === 0 ? 'Your retirement' : "Partner's retirement",
            notes: [],
        });
    });

    accounts.push({
        id: 'brokerage',
        memberLabel: 'Taxable brokerage',
        accountType: 'brokerage',
        ownership: investment.isJoint ? 'joint' : 'primary',
        rothEligibility: 'direct',
        annualLimitCents: null,
        annualAllocationCents: brokerageAnnualCents,
        startingCashCents: brokerageCashCents,
        existingBalanceCents: 0,
        projectedBalanceCents: 0,
        label: investment.isJoint ? 'Joint brokerage' : 'Your brokerage',
        notes: [],
    });

    for (const acct of accounts) {
        const monthly = Math.round(acct.annualAllocationCents / 12);
        acct.projectedBalanceCents = computeProjectedTotal(
            monthly,
            annualReturnPercent,
            years,
            acct.startingCashCents + acct.existingBalanceCents,
        );
    }

    const existingIraAccounts: ExistingIraAccount[] = [];
    ownershipKeys.forEach((ownerKey, index) => {
        const balanceCents = sumAccountBalance('ira', ownerKey);
        if (balanceCents <= 0) return;

        existingIraAccounts.push({
            id: `existing-ira-${index}`,
            label:
                index === 0
                    ? 'Your existing Traditional IRA'
                    : "Partner's existing Traditional IRA",
            accountTypeLabel: 'Traditional IRA',
            startingBalanceCents: balanceCents,
            // Grow at the assumed return with no new contributions.
            projectedBalanceCents: computeProjectedTotal(
                0,
                annualReturnPercent,
                years,
                balanceCents,
            ),
        });
    });

    const startingBalanceCents = existingAccountsList
        .filter((a) => a.accountType === 'ira' || a.accountType === 'roth-ira')
        .reduce((sum, a) => sum + Math.max(0, a.balanceCents ?? 0), 0);

    // Existing assets + planned cash both compound from day one.
    const totalStartingCents = startingBalanceCents + plannedCashCents;
    const monthlyContribution = Math.round(totalCapacityCents / 12);
    const projectedTotalCents = computeProjectedTotal(
        monthlyContribution,
        annualReturnPercent,
        years,
        totalStartingCents,
    );

    const bufferedIncomeCents = Math.round(
        incomeCents * INCOME_BUFFER_MULTIPLIER,
    );

    return {
        accounts,
        existingIraAccounts,
        totalAnnualContributionCents: totalCapacityCents,
        startingBalanceCents,
        plannedStartingCashContributionCents: plannedCashCents,
        projectedTotalCents,
        projectedDate: formatProjectedDate(years),
        projectedYears: years,
        annualReturnPercent,
        feeComparison: computeFeeComparison(
            totalStartingCents,
            totalCapacityCents,
        ),
        rothDetails: {
            filingStatus: filingKey,
            incomeCents,
            bufferedIncomeCents,
            phaseOutStartCents: phaseOutStart,
            useBackdoor,
        },
    };
}
